import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { demandAPI, authAPI, locationAPI } from '../../services/api';

export default function BuyerAccount() {
  const { user, login, logout } = useAuth();
  const [stats, setStats] = useState({ posted: 0, offers: 0, closed: 0, responseRate: 0 });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ fullName: '', mobileNumber: '', state: '', city: '' });
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  useEffect(() => {
    demandAPI.mine().then(r => {
      const list = r.data.data || [];
      setStats({
        posted: list.length,
        closed: list.filter(d => d.status === 'closed').length,
        offers: list.reduce((s, d) => s + (d.offersCount || 0), 0),
        responseRate: list.length > 0 ? Math.round((list.filter(d => d.offersCount > 0).length / list.length) * 100) : 0,
      });
    }).catch(() => { });
    locationAPI.states().then(r => setStates(r.data.data || [])).catch(() => { });
  }, []);

  useEffect(() => {
    if (form.state) locationAPI.cities(form.state).then(r => setCities(r.data.data || [])).catch(() => setCities([]));
    else setCities([]);
  }, [form.state]);

  const startEdit = () => {
    setForm({ fullName: user?.fullName || '', mobileNumber: user?.mobileNumber || '', state: user?.state || '', city: user?.city || '' });
    setEditing(true);
  };

  const handleSave = async () => {
    // 1. Full Name Validation
    const trimmedName = form.fullName ? form.fullName.trim() : '';
    if (!trimmedName) {
      alert('Full Name cannot be empty.');
      return;
    }
    if (trimmedName.length < 3 || trimmedName.length > 50) {
      alert('Full Name must be between 3 and 50 characters.');
      return;
    }
    const nameRegex = /^[a-zA-Z\s.-]+$/;
    if (!nameRegex.test(trimmedName)) {
      alert('Full Name can only contain letters, spaces, dots, or hyphens.');
      return;
    }

    // 2. Mobile Number Validation
    let cleanedMobile = form.mobileNumber ? form.mobileNumber.replace(/\D/g, '') : '';
    if (cleanedMobile.length === 12 && cleanedMobile.startsWith('91')) {
      cleanedMobile = cleanedMobile.slice(2);
    } else if (cleanedMobile.length === 11 && cleanedMobile.startsWith('0')) {
      cleanedMobile = cleanedMobile.slice(1);
    }
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobileRegex.test(cleanedMobile)) {
      alert('Please enter a valid 10-digit Indian mobile number.');
      return;
    }

    // 3. State & City Validation
    if (!form.state) {
      alert('Please select a State.');
      return;
    }
    if (!form.city) {
      alert('Please select a City.');
      return;
    }

    setSaving(true);
    try {
      const res = await authAPI.updateProfile({
        fullName: trimmedName,
        mobileNumber: cleanedMobile,
        state: form.state,
        city: form.city
      });
      const token = localStorage.getItem('kanjusee_token');
      if (token) login(token, res.data.data);
      setEditing(false);
    } catch (err) { alert(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
    setDeleteSuccess(false);
  };

  const confirmDeleteAccount = async () => {
    setDeleting(true);
    try {
      await authAPI.deleteAccount();
      setDeleteSuccess(true);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete account.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 22, marginBottom: 20 }}>My Account</h2>

      <div className="card" style={{ marginBottom: 20, padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--green-lt)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: 'var(--green)' }}>
              {user?.fullName?.charAt(0) || 'U'}
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{user?.fullName}</div>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>{user?.email}</div>
            </div>
          </div>
          {!editing && <button onClick={startEdit} style={{ background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 9, padding: '8px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Edit Profile</button>}
        </div>

        {!editing ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {[['Full Name', user?.fullName], ['Email', user?.email], ['Mobile', user?.mobileNumber || '—'], ['State', user?.state || 'Not set'], ['City', user?.city || 'Not set'], ['Role', user?.role]].map(([k, v], i) => (
              <div key={i}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{k}</div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{v}</div>
              </div>
            ))}
          </div>
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group"><label className="form-label">Full Name</label><input className="form-input" value={form.fullName} onChange={e => setForm({ ...form, fullName: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Mobile</label><input className="form-input" value={form.mobileNumber} onChange={e => setForm({ ...form, mobileNumber: e.target.value })} /></div>
              <div className="form-group">
                <label className="form-label">State</label>
                <select className="form-input" value={form.state} onChange={e => setForm({ ...form, state: e.target.value, city: '' })} style={{ cursor: 'pointer' }}>
                  <option value="">Select State</option>
                  {states.map(s => <option key={s.state || s} value={s.state || s}>{s.state || s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">City</label>
                <select className="form-input" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} disabled={!form.state} style={{ cursor: 'pointer' }}>
                  <option value="">{form.state ? 'Select City' : 'Select state first'}</option>
                  {cities.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button onClick={() => setEditing(false)} style={{ background: '#fff', border: '1.5px solid var(--border)', borderRadius: 9, padding: '8px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: 'var(--muted)' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 9, padding: '8px 22px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </div>
        )}

        {!user?.city && !editing && (
          <div style={{ background: 'var(--gold-lt)', border: '1px solid var(--gold)', borderRadius: 9, padding: '10px 14px', marginTop: 14, fontSize: 12, color: 'var(--gold-dk)' }}>
            ⚠️ Set your city & state to auto-fill location when posting demands.
          </div>
        )}
      </div>

      {/* Total Demands Posted */}
      <div className="card" style={{ marginBottom: 20, padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 6 }}>Total Demands Posted</div>
            <div style={{ fontFamily: 'var(--font-serif)', fontSize: 36, fontWeight: 700, color: 'var(--green)' }}>{stats.posted}</div>
          </div>
          <div style={{ width: 70, height: 70, borderRadius: '50%', border: '4px solid var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: 'var(--green)' }}>📋</div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>This indicates the total number of demands you have posted on Kanjusee.</div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[{ l: 'Demands', v: stats.posted, c: 'var(--green)' }, { l: 'Offers', v: stats.offers, c: 'var(--gold)' }, { l: 'Closed', v: stats.closed, c: 'var(--green)' }, { l: 'Response', v: `${stats.responseRate}%`, c: 'var(--char)' }].map((s, i) => (
          <div key={i} className="card" style={{ padding: 16, textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 700, color: s.c }}>{s.v}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Account Deletion */}
      <div className="card" style={{ padding: 24, border: '1px solid #FEE2E2', background: '#FEF2F2' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#991B1B', marginBottom: 8 }}>Account Deletion</div>
        <p style={{ fontSize: 13, color: '#7F1D1D', marginBottom: 16, lineHeight: 1.5 }}>
          Once you delete your account, there is no going back. Please be certain. All your demands and data will be permanently deactivated.
        </p>
        <button
          onClick={handleDeleteAccount}
          style={{ background: '#DC2626', color: '#fff', border: 'none', borderRadius: 9, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
        >
          Delete Account
        </button>
      </div>

      {/* Modern Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '12px',
            width: '90%',
            maxWidth: '370px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            textAlign: 'center'
          }}>
            {!deleteSuccess ? (
              <>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: '#FEE2E2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  color: '#DC2626'
                }}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                </div>
                <h3 style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: '22px',
                  fontWeight: 700,
                  color: '#1A1A1A',
                  marginBottom: '12px'
                }}>
                  Delete Account?
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--muted)',
                  lineHeight: '1.6',
                  marginBottom: '24px'
                }}>
                  Are you sure you want to permanently delete your account? This action cannot be undone and all your demands and data will be permanently deactivated.
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      borderRadius: '9px',
                      border: '1.5px solid var(--border)',
                      background: '#fff',
                      color: 'var(--muted)',
                      fontSize: '14px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteAccount}
                    disabled={deleting}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      borderRadius: '9px',
                      border: 'none',
                      background: '#DC2626',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      opacity: deleting ? 0.6 : 1,
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => !deleting && (e.currentTarget.style.backgroundColor = '#B91C1C')}
                    onMouseLeave={(e) => !deleting && (e.currentTarget.style.backgroundColor = '#DC2626')}
                  >
                    {deleting ? 'Deleting...' : 'Delete Account'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--green-lt)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  color: 'var(--green)'
                }}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <h3 style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: '22px',
                  fontWeight: 700,
                  color: '#1A1A1A',
                  marginBottom: '12px'
                }}>
                  Account Deleted
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--muted)',
                  lineHeight: '1.6',
                  marginBottom: '24px'
                }}>
                  Your account has been deleted successfully.
                </p>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    logout();
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 20px',
                    borderRadius: '9px',
                    border: 'none',
                    background: 'var(--green)',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--green-dk)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--green)'}
                >
                  OK
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
